import {Injectable, inject, DOCUMENT} from '@angular/core';
import {Meta, Title} from '@angular/platform-browser';
import {environment} from '../../../environments/environment';

/** Per-page search and social metadata, set as `data: { seo: ... }` on a route. */
export interface PageSeo {
  /** Full page title, about 60 characters at most so search results don't truncate it. */
  title: string;
  /** About 150 characters: what the page offers, in words a searcher would use. */
  description: string;
  /** Keep a public but low-value page (login, sign-up) out of search results. */
  noindex?: boolean;
}

export const DEFAULT_SEO: PageSeo = {
  title: 'Talespinner | AI writing studio for novels and stories',
  description: 'Write your novel with an AI that keeps your characters, world and outline in mind. '
    + 'Draft chapter by chapter in any genre or language. Free Quills to start.',
};

/**
 * Writes the title, description, robots, canonical, Open Graph and Twitter tags for the current page.
 * Runs during prerendering too, so the static HTML that crawlers and link previews read is complete.
 */
@Injectable({providedIn: 'root'})
export class SeoService {

  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);

  private readonly siteUrl = environment.clientUrl.replace(/\/$/, '');
  /** 1200×630 share card, rendered from the home page hero. */
  private readonly shareImage = `${this.siteUrl}/assets/images/og-card.png`;

  apply(seo: PageSeo | undefined, url: string) {
    const page = {...DEFAULT_SEO, ...seo};
    const canonical = this.siteUrl + this.pathOf(url);
    const indexable = environment.allowSearchEngineIndexing && !page.noindex;

    this.title.setTitle(page.title);
    this.meta.updateTag({name: 'description', content: page.description});
    this.meta.updateTag({name: 'robots', content: indexable ? 'index, follow' : 'noindex, nofollow'});
    this.setCanonical(canonical);

    this.meta.updateTag({property: 'og:type', content: 'website'});
    this.meta.updateTag({property: 'og:site_name', content: 'Talespinner'});
    this.meta.updateTag({property: 'og:locale', content: 'en_US'});
    this.meta.updateTag({property: 'og:title', content: page.title});
    this.meta.updateTag({property: 'og:description', content: page.description});
    this.meta.updateTag({property: 'og:url', content: canonical});
    this.meta.updateTag({property: 'og:image', content: this.shareImage});
    this.meta.updateTag({property: 'og:image:width', content: '1200'});
    this.meta.updateTag({property: 'og:image:height', content: '630'});
    this.meta.updateTag({property: 'og:image:alt', content: 'Talespinner: every tale begins with a single thread'});

    this.meta.updateTag({name: 'twitter:card', content: 'summary_large_image'});
    this.meta.updateTag({name: 'twitter:site', content: '@TalespinnerIO'});
    this.meta.updateTag({name: 'twitter:title', content: page.title});
    this.meta.updateTag({name: 'twitter:description', content: page.description});
    this.meta.updateTag({name: 'twitter:image', content: this.shareImage});
  }

  /** The path alone: query strings and fragments would split one page into many canonical URLs. */
  private pathOf(url: string): string {
    const path = url.split(/[?#]/)[0];
    return path === '' ? '/' : path;
  }

  private setCanonical(href: string) {
    let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }
}
